import React, { useState, ChangeEvent, useEffect } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { AiOutlineDelete } from "react-icons/ai";
import editProfileUser from "../../../assets/images/others/host-side-images/editProfileUSer.png";
import { ReactComponent as EditProfilePersonal } from "../../../assets/images/svgs/edit-profiles.svg";
import AttendeeBreadcrumb, {
  AttendeBreadcrumbLink,
} from "../../../components/breadcrumbs/AttendeeBreadcrumb";
import { siteRoutes } from "../../../utils/helpers/enums/routes";
import useStore from "hooks/useStore";
import { GENDERS, LANGUAGES } from "utils/helpers/enums/shared.enum";
import { CompleteAttendeeProfileDTO } from "utils/helpers/models/attendee/complete-profile.dto";
import useAttendee from "../useHooks";
import useAuth from "containers/auth/useHooks";

const AttendeeEditProfile: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CompleteAttendeeProfileDTO>(
    new CompleteAttendeeProfileDTO()
  );
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const { EditProfileAttendee, uploadFile } = useAttendee();
  const { getStates } = useAuth();
  const breadcrumbLinks: AttendeBreadcrumbLink[] = [
    { title: "Home", path: siteRoutes.attendeeHome },
    { title: "Edit Profile", path: siteRoutes.editAttendeeProfile },
  ];

  // Use single method to get all user data
  const { getUserData, isProfileCompleted } = useStore();

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.attendeeHome, { replace: true });
    }
  }, [isProfileCompleted, navigate]);
  const userData = getUserData();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<CompleteAttendeeProfileDTO>({
    defaultValues: {
      firstName: userData?.firstName || "",
      lastName: userData?.lastName || "",
      email: userData?.email || "",
      city: userData?.city || "",
      state: userData?.state || "",
      address: userData?.address || "",
      language: userData?.language || "",
      dob: userData?.dob || "",
      gender: userData?.gender || "",
      phone: userData?.phone || "",
      zipCode: userData?.zipCode || "",
      profilePicId: userData?.profilePicId || 0,
    },
  });

  const [profileImage, setProfileImage] = useState<string | null>(
    userData?.profilePicUrl || null
  );

  // Load states data and initialize formData on mount
  React.useEffect(() => {
    getStates(setStates);

    // Initialize formData with values from store
    const initialData = new CompleteAttendeeProfileDTO();
    initialData.firstName = userData?.firstName || "";
    initialData.lastName = userData?.lastName || "";
    initialData.email = userData?.email || "";
    initialData.city = userData?.city || "";
    initialData.state = userData?.state || "";
    initialData.address = userData?.address || "";
    initialData.language = userData?.language || "";
    initialData.dob = userData?.dob || "";
    initialData.gender = userData?.gender || "";
    initialData.phone = userData?.phone || "";
    initialData.zipCode = userData?.zipCode || "";
    initialData.profilePicId = userData?.profilePicId || 0;

    setFormData(initialData);
  }, []);

  // Load cities when state changes
  React.useEffect(() => {
    if (formData.state && states.length > 0) {
      const state = states.find((s) => s.name === formData.state);
      if (state) {
        setCities([...state.cities]);
      }
    }
  }, [formData.state, states]);

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setProfileImage(previewUrl);
      setValue("profilePicUrl", file.name, { shouldValidate: true });

      try {
        const uploaded = await uploadFile(file);
        if (uploaded?.id) {
          setValue("profilePicId", uploaded.id, { shouldValidate: true });
        }
      } catch (error) {
        console.error("Upload failed", error);
      }
    }
  };

  const handleImageDelete = () => {
    setProfileImage(null);
    setValue("profilePicUrl", "");
  };

  const handleChange = (event: any) => {
    const { value, name } = event.target;
    formData[name] = value;
    setValue(name, formData[name], { shouldValidate: true });

    // Clear errors when user starts typing
    if (errors[name]) {
      clearErrors(name);
    }

    if (name === "state") {
      formData.city = "";
      setValue("city", "");
      clearErrors("city");
    }
    setFormData({ ...formData });
  };

  const onSubmit: SubmitHandler<CompleteAttendeeProfileDTO> = (data) => {
    EditProfileAttendee(data);
  };

  return (
    <div className="container mb-4">
      <AttendeeBreadcrumb links={breadcrumbLinks} />

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Profile Picture */}
        <div className="flex flex-col  mb-8">
          <div className="relative w-36 h-36">
            <img
              src={profileImage || editProfileUser}
              alt="Profile"
              className="w-36 h-36 rounded-full object-cover"
            />
            {/* Button / Label */}
            {profileImage ? (
              <button
                type="button"
                onClick={() => document.getElementById("profileImage")?.click()}
                className="absolute bottom-2 right-1 bg-white hover:bg-gray-100 text-gray-700 p-2 rounded-full shadow-md cursor-pointer"
              >
                <EditProfilePersonal />
              </button>
            ) : (
              <label
                htmlFor="profileImage"
                className="absolute bottom-2 right-1 bg-white hover:bg-gray-100 text-gray-700 p-2 rounded-full shadow-md cursor-pointer"
              >
                <EditProfilePersonal />
              </label>
            )}
            <input
              id="profileImage"
              type="file"
              accept="image/*"
              className="hidden"
              {...register("profilePicId", {
                required: false,
              })}
              onChange={handleImageChange}
            />
          </div>
          {errors.profilePicId && (
            <p className="text-red-500 text-sm mt-2">
              {errors.profilePicId.message}
            </p>
          )}
          <p className="text-base text-blackie font-semibold mt-4">Upload Profile Picture</p>
        </div>

        {/* Form Fields */}
        <div className="w-full my-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* First Name */}
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              First name
            </label>
            <input
              type="text"
              className="w-full h-[51px] px-2 rounded-lg border border-inputsBorderColor outline-none"
              {...register("firstName", { required: "First name is required" })}
            />
            {errors.firstName && (
              <p className="text-red-500 text-sm">{errors.firstName.message}</p>
            )}
          </div>

          {/* Last Name */}
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Last name
            </label>
            <input
              type="text"
              className="w-full h-[51px] px-2 rounded-lg border border-inputsBorderColor outline-none"
              {...register("lastName", { required: "Last name is required" })}
            />
            {errors.lastName && (
              <p className="text-red-500 text-sm">{errors.lastName.message}</p>
            )}
          </div>
          {/* Email */}
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">Email</label>
            <input
              type="email"
              className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: "Enter a valid email",
                },
              })}
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && (
              <p className="text-red-500 text-sm">{errors.email.message}</p>
            )}
          </div>

          {/* Date of Birth */}
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Date of Birth
            </label>
            <input
              type="date"
              placeholder="Select Date"
              className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
              {...register("dob", {
                required: "Date of Birth is required",
                validate: (value) => {
                  if (!value) return "Date of Birth is required";
                  const selectedDate = new Date(value);
                  const currentDate = new Date();
                  const age =
                    currentDate.getFullYear() - selectedDate.getFullYear();
                  const monthDiff =
                    currentDate.getMonth() - selectedDate.getMonth();

                  // Calculate exact age considering month and day
                  const exactAge =
                    monthDiff < 0 ||
                    (monthDiff === 0 &&
                      currentDate.getDate() < selectedDate.getDate())
                      ? age - 1
                      : age;

                  if (exactAge < 13) {
                    return "Minimum age required is 13 years";
                  }
                  return true;
                },
              })}
              max={(() => {
                const currentDate = new Date();
                const maxDate = new Date(
                  currentDate.getFullYear() - 13,
                  currentDate.getMonth(),
                  currentDate.getDate()
                );
                return maxDate.toISOString().split("T")[0];
              })()}
              value={formData.dob}
              onChange={handleChange}
            />
            {errors.dob && (
              <p className="text-red-500 text-sm">{errors.dob.message}</p>
            )}
          </div>

          {/* Gender */}
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">Gender</label>
            <select
              className="w-full h-[51px] px-2 rounded-lg border border-inputsBorderColor outline-none"
              {...register("gender", { required: "Gender is required" })}
            >
              <option value="">Select Gender</option>
              {Object.values(GENDERS).map((gender, index) => {
                return (
                  <option className="capitalize" value={gender} key={index}>
                    {gender}
                  </option>
                );
              })}
            </select>
            {errors.gender && (
              <p className="text-red-500 text-sm">{errors.gender.message}</p>
            )}
          </div>

          {/* Language */}
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Language
            </label>
            <select
              className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
              {...register("language", { required: "Language is required" })}
            >
              <option value="">Select Language</option>
              {Object.values(LANGUAGES).map((lang, index) => {
                return <option key={index}>{lang}</option>;
              })}
            </select>
            {errors.language && (
              <p className="text-red-500 text-sm">{errors.language.message}</p>
            )}
          </div>

          {/* Address */}
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Address
            </label>
            <input
              type="text"
              className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor  outline-none"
              {...register("address", {
                required: "Address is required",
              })}
              value={formData.address}
              onChange={handleChange}
            />
            {errors.address && (
              <p className="text-red-500 text-sm">{errors.address.message}</p>
            )}
          </div>

          {/* State Dropdown */}
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">State</label>
            <select
              className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
              {...register("state", {
                required: "State is required",
              })}
              value={formData.state}
              onChange={handleChange}
            >
              <option value="" disabled>
                Select
              </option>
              {states.map((state, index) => (
                <option key={index} value={state.name}>
                  {state.name}
                </option>
              ))}
            </select>
            {errors.state && (
              <p className="text-red-500 text-sm">{errors.state.message}</p>
            )}
          </div>

          {/* City Dropdown */}
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">City</label>
            <select
              className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
              {...register("city", {
                required: "City is required",
              })}
              value={formData.city}
              onChange={handleChange}
            >
              <option value="" disabled>
                Select
              </option>
              {cities.map((city, index) => (
                <option key={index} value={city.name}>
                  {city.name}
                </option>
              ))}
            </select>
            {errors.city && (
              <p className="text-red-500 text-sm">{errors.city.message}</p>
            )}
          </div>

          {/* Zip Code */}
          <div className="flex flex-col gap-1">
            <label className="text-label text-base font-semibold">
              Zip Code
            </label>
            <input
              type="text"
              className="w-full h-[51px] px-2 rounded-lg border border-inputsBorderColor outline-none"
              {...register("zipCode", { required: "Zip Code is required" })}
            />
            {errors.zipCode && (
              <p className="text-red-500 text-sm">{errors.zipCode.message}</p>
            )}
          </div>
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-primaryBlueBg text-white rounded-full mt-4"
        >
          Update
        </button>
      </form>
    </div>
  );
};

export default AttendeeEditProfile;
